// ─── Search Results Route ───
// Full-page, paginated Global Search results — the "View all results"
// destination from the ⌘K Command Palette. See
// docs/design/components/global-search.md ("Surface 2 — /app/search results
// page") and apps/web/src/routes/app/settings.tsx for the page-shell
// convention this route follows.

import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { SearchTypeEnum } from '@a8-planner/shared/schemas/search.schema';
import { SearchResultsPage } from '../../features/search/SearchResultsPage';

// `q` is deliberately lenient (plain optional string, no min-length) unlike
// the API's SearchQuerySchema (`q.min(2)`) — the ROUTE must also represent
// "no query yet" (empty/absent) and single characters while the user is
// mid-type, neither of which the API schema allows. `types` reuses the exact
// SearchTypeEnum from the shared contract; only a single value is ever
// stored here (the results page chips are single-select — see
// global-search.md's filter chip section), so no CSV parsing is needed.
const searchPageSearchSchema = z.object({
  q: z.string().optional(),
  types: SearchTypeEnum.optional(),
  /** Workspace narrowing scope (uuid). Absent = all accessible workspaces —
   * shareable URLs must not depend on the viewer's active-workspace state. */
  workspace: z.string().uuid().optional().catch(undefined),
  /** ADR 0016 + scope-tree-picker.md resolution #1 — company scope
   * narrowing. Absent = the viewer's active company (via the TopBar
   * switcher); `'all'` = merged across every accessible company; a uuid = a
   * SPECIFIC company browsed locally via the scope tree, independent of the
   * TopBar's active company (a local filter only — no global company switch). */
  company: z
    .union([z.literal('all'), z.string().uuid()])
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute('/app/search')({
  validateSearch: searchPageSearchSchema,
  component: SearchResultsPage,
});
